<template>
  <div>
    <div class="page-head">
      <div>
        <h2>小程序用户</h2>
        <p>查看和维护小程序用户资料、手机号和账号状态。</p>
      </div>
    </div>

    <el-card shadow="never">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="关键词"><el-input v-model="filters.keyword" placeholder="客户编号/昵称/手机号/OpenID" clearable /></el-form-item>
        <el-form-item label="账号状态">
          <el-select v-model="filters.status" placeholder="全部" clearable>
            <el-option label="正常" value="active" />
            <el-option label="停用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item><el-button type="primary" @click="fetchList">查询</el-button><el-button @click="reset">重置</el-button></el-form-item>
      </el-form>

      <paged-table :rows="rows" :loading="loading" :page="page" :page-size="pageSize" :total="total" @page-change="page = $event; fetchList()" @size-change="pageSize = $event; page = 1; fetchList()">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="customer_no" label="客户编号" width="210" />
        <el-table-column label="头像" width="76">
          <template slot-scope="{ row }">
            <el-avatar :size="36" :src="row.avatar_url">{{ (row.nickname || '用').slice(0, 1) }}</el-avatar>
          </template>
        </el-table-column>
        <el-table-column prop="nickname" label="昵称" min-width="160" />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="openid" label="OpenID" min-width="220" show-overflow-tooltip />
        <el-table-column prop="unionid" label="UnionID" min-width="180" show-overflow-tooltip />
        <el-table-column label="推荐官" width="150">
          <template slot-scope="{ row }">
            <el-tag v-if="row.is_recommender" type="success">是</el-tag>
            <el-tag v-else type="info">否</el-tag>
            <span v-if="row.recommender_code" class="code-text">{{ row.recommender_code }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="账号状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.status" /></template></el-table-column>
        <el-table-column label="最近登录" width="180"><template slot-scope="{ row }">{{ row.last_login_at | dateTime }}</template></el-table-column>
        <el-table-column label="注册时间" width="180"><template slot-scope="{ row }">{{ row.created_at | dateTime }}</template></el-table-column>
        <el-table-column label="操作" width="290" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="openEdit(row)">编辑</el-button>
            <el-button type="text" @click="openBusinessRecords(row)">业务档案</el-button>
            <el-button v-if="!row.is_recommender" type="text" @click="setRecommender(row)">设为推荐官</el-button>
            <template v-else>
              <el-button type="text" @click="openReferralRecords(row)">推荐记录</el-button>
              <el-button type="text" class="danger-text" @click="unsetRecommender(row)">取消推荐官</el-button>
            </template>
          </template>
        </el-table-column>
      </paged-table>
    </el-card>

    <el-dialog title="编辑小程序用户" :visible.sync="drawerVisible" width="520px" destroy-on-close>
      <el-form class="drawer-form" :model="form" label-width="110px">
        <el-form-item label="客户编号"><el-input v-model="form.customer_no" disabled /></el-form-item>
        <el-form-item label="昵称"><el-input v-model="form.nickname" /></el-form-item>
        <el-form-item label="手机号"><el-input v-model="form.phone" /></el-form-item>
        <el-form-item label="头像"><asset-upload v-model="form.avatar_url" type="image" category="users" /></el-form-item>
        <el-form-item label="账号状态">
          <el-select v-model="form.status">
            <el-option label="正常" value="active" />
            <el-option label="停用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-alert
          v-if="form.status === 'inactive'"
          title="停用后，该用户所有已登录设备会立即失效，个人资料、需求、咨询、订单、收藏和评价接口均不可继续访问。恢复后需要重新登录。"
          type="warning"
          :closable="false"
          show-icon
        />
      </el-form>
      <div class="drawer-footer">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存用户</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="客户业务档案"
      :visible.sync="businessVisible"
      width="1080px"
      append-to-body
      :lock-scroll="false"
      @opened="layoutBusinessTables"
    >
      <div v-loading="businessLoading">
        <div class="record-head">
          <span>客户：{{ businessCustomer.nickname || '-' }}</span>
          <span>客户编号：{{ businessCustomer.customer_no || '-' }}</span>
          <span>手机号：{{ businessCustomer.phone || '-' }}</span>
        </div>
        <div class="summary-grid business-summary">
          <div><strong>{{ businessSummary.consultation_total || 0 }}</strong><span>咨询记录</span></div>
          <div><strong>{{ businessSummary.demand_total || 0 }}</strong><span>定制需求</span></div>
          <div><strong>{{ businessSummary.order_total || 0 }}</strong><span>订单总数</span></div>
          <div><strong>{{ businessSummary.paid_order_total || 0 }}</strong><span>成交订单</span></div>
          <div><strong>¥{{ money(businessSummary.paid_amount) }}</strong><span>成交金额</span></div>
        </div>
        <el-tabs v-model="businessActiveTab" @tab-click="layoutBusinessTables">
          <el-tab-pane label="咨询记录" name="consultations" lazy>
            <el-table ref="businessConsultationsTable" :data="businessConsultations" border size="small" max-height="52vh" empty-text="暂无咨询记录">
              <el-table-column prop="consultation_no" label="咨询编号" min-width="205" />
              <el-table-column prop="name" label="咨询名称" min-width="160" />
              <el-table-column prop="source_page" label="来源" min-width="130" />
              <el-table-column prop="status" label="状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.status" /></template></el-table-column>
              <el-table-column label="咨询时间" width="180"><template slot-scope="{ row }">{{ row.created_at | dateTime }}</template></el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane label="定制需求" name="demands" lazy>
            <el-table ref="businessDemandsTable" :data="businessDemands" border size="small" max-height="52vh" empty-text="暂无定制需求">
              <el-table-column prop="name" label="需求名称" min-width="180" />
              <el-table-column prop="source_title" label="来源" min-width="180" />
              <el-table-column label="出行日期" width="130"><template slot-scope="{ row }">{{ row.travel_date | date }}</template></el-table-column>
              <el-table-column prop="status" label="状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.status" /></template></el-table-column>
              <el-table-column label="提交时间" width="180"><template slot-scope="{ row }">{{ row.created_at | dateTime }}</template></el-table-column>
            </el-table>
          </el-tab-pane>
          <el-tab-pane label="订单记录" name="orders" lazy>
            <el-table ref="businessOrdersTable" :data="businessOrders" border size="small" max-height="52vh" empty-text="暂无订单记录">
              <el-table-column prop="order_no" label="订单号" min-width="205" />
              <el-table-column prop="consultation_no" label="来源咨询" min-width="205"><template slot-scope="{ row }">{{ row.consultation_no || '-' }}</template></el-table-column>
              <el-table-column prop="name" label="订单名称" min-width="160" />
              <el-table-column prop="amount" label="金额" width="100" />
              <el-table-column prop="payment_status" label="支付状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.payment_status" /></template></el-table-column>
              <el-table-column label="下单时间" width="180"><template slot-scope="{ row }">{{ row.created_at | dateTime }}</template></el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>

    <el-dialog title="推荐官业务明细" :visible.sync="recordsVisible" width="1000px">
      <div class="record-head">
        <span>推荐官：{{ activeUser.nickname || '-' }}</span>
        <span>推荐码：{{ activeUser.recommender_code || '-' }}</span>
      </div>
      <div class="summary-grid">
        <div><strong>{{ referralSummary.binding_total || 0 }}</strong><span>绑定用户</span></div>
        <div><strong>{{ referralSummary.order_total || 0 }}</strong><span>推荐订单</span></div>
        <div><strong>{{ referralSummary.paid_order_total || 0 }}</strong><span>成交订单</span></div>
        <div><strong>¥{{ money(referralSummary.paid_amount) }}</strong><span>成交金额</span></div>
        <div><strong>¥{{ money(referralSummary.pending_commission) }}</strong><span>待结算佣金</span></div>
        <div><strong>¥{{ money(referralSummary.settled_commission) }}</strong><span>已结算佣金</span></div>
      </div>
      <el-tabs v-loading="recordsLoading">
        <el-tab-pane label="绑定用户">
          <el-table :data="referralRecords" border size="small">
            <el-table-column prop="nickname" label="被推荐用户" min-width="140" />
            <el-table-column prop="phone" label="手机号（脱敏）" width="140" />
            <el-table-column prop="openid" label="OpenID" min-width="210" show-overflow-tooltip />
            <el-table-column prop="bind_source" label="绑定来源" width="120"><template slot-scope="{ row }">{{ bindSourceText(row.bind_source) }}</template></el-table-column>
            <el-table-column label="绑定时间" width="180"><template slot-scope="{ row }">{{ row.bind_time | dateTime }}</template></el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="归因订单">
          <el-table :data="referralOrders" border size="small">
            <el-table-column prop="order_no" label="订单号" min-width="185" />
            <el-table-column prop="user_nickname" label="用户" width="120" />
            <el-table-column prop="amount" label="金额" width="100" />
            <el-table-column prop="payment_status" label="支付状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.payment_status" /></template></el-table-column>
            <el-table-column prop="attribution_source" label="归因来源" min-width="170" />
            <el-table-column prop="staff_name" label="协同员工" width="120" />
            <el-table-column label="下单时间" width="180"><template slot-scope="{ row }">{{ row.created_at | dateTime }}</template></el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="佣金记录">
          <el-table :data="referralCommissions" border size="small">
            <el-table-column prop="order_no" label="订单号" min-width="180" />
            <el-table-column prop="amount" label="佣金" width="100" />
            <el-table-column label="比例" width="90"><template slot-scope="{ row }">{{ row.commission_rate === null ? '-' : `${Number(row.commission_rate) * 100}%` }}</template></el-table-column>
            <el-table-column prop="status" label="状态" width="105"><template slot-scope="{ row }"><status-tag :value="row.status" /></template></el-table-column>
            <el-table-column label="结算时间" width="180"><template slot-scope="{ row }">{{ row.settled_at | dateTime }}</template></el-table-column>
            <el-table-column prop="remark" label="说明" min-width="160" />
            <el-table-column label="操作" width="125"><template slot-scope="{ row }"><template v-if="row.status === 'pending'"><el-button type="text" @click="settleCommission(row)">结算</el-button><el-button type="text" class="danger-text" @click="cancelCommission(row)">取消</el-button></template></template></el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script>
import AssetUpload from "../components/AssetUpload.vue";
import PagedTable from "../components/PagedTable.vue";
import StatusTag from "../components/StatusTag.vue";
import { listResource, updateResource } from "../api/resources";
import http from "../api/http";

export default {
  name: "UsersManage",
  components: { AssetUpload, PagedTable, StatusTag },
  data() {
    return {
      loading: false,
      saving: false,
      drawerVisible: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      form: {},
      recordsVisible: false,
      recordsLoading: false,
      activeUser: {},
      originalStatus: "",
      referralRecords: [],
      referralOrders: [],
      referralCommissions: [],
      referralSummary: {},
      businessVisible: false,
      businessLoading: false,
      businessActiveTab: "consultations",
      businessCustomer: {},
      businessSummary: {},
      businessConsultations: [],
      businessDemands: [],
      businessOrders: []
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        const res = await listResource("users", { ...this.filters, page: this.page, pageSize: this.pageSize });
        this.rows = res.data && res.data.items ? res.data.items : [];
        this.total = res.data && res.data.total ? res.data.total : 0;
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.filters = { keyword: "", status: "" };
      this.page = 1;
      this.fetchList();
    },
    openEdit(row) {
      this.form = { ...row };
      this.originalStatus = row.status;
      this.drawerVisible = true;
    },
    async save() {
      if (this.originalStatus !== "inactive" && this.form.status === "inactive") {
        await this.$confirm(
          "停用后该用户所有现有登录会话会立即失效，且无法继续访问个人及写入接口。确定停用吗？",
          "停用小程序用户",
          { type: "warning", confirmButtonText: "确认停用" }
        );
      }
      this.saving = true;
      try {
        await updateResource("users", this.form.id, this.form);
        const message = this.originalStatus !== this.form.status
          ? (this.form.status === "inactive" ? "用户已停用，现有登录会话已失效" : "用户已恢复，需重新登录后使用")
          : "用户资料已保存";
        this.$message.success(message);
        this.drawerVisible = false;
        this.fetchList();
      } finally {
        this.saving = false;
      }
    },
    async setRecommender(row) {
      await http.post(`/users/${row.id}/recommender`, {
        name: row.nickname,
        phone: row.phone
      });
      this.$message.success("已设为推荐官");
      this.fetchList();
    },
    async unsetRecommender(row) {
      await this.$confirm("确定取消该用户的推荐官身份吗？已绑定的推荐关系会保留。", "取消推荐官", { type: "warning" });
      await http.post(`/users/${row.id}/recommender/cancel`);
      this.$message.success("已取消推荐官");
      this.fetchList();
    },
    async openReferralRecords(row) {
      this.activeUser = { ...row };
      this.referralRecords = [];
      this.referralOrders = [];
      this.referralCommissions = [];
      this.referralSummary = {};
      this.recordsVisible = true;
      this.recordsLoading = true;
      try {
        const res = await http.get(`/users/${row.id}/referrals`);
        const data = res.data || {};
        this.referralRecords = data.items || [];
        this.referralOrders = data.orders || [];
        this.referralCommissions = data.commissions || [];
        this.referralSummary = data.summary || {};
      } finally {
        this.recordsLoading = false;
      }
    },
    async openBusinessRecords(row) {
      if (this.businessLoading) return;
      this.businessLoading = true;
      this.businessActiveTab = "consultations";
      this.businessCustomer = { ...row };
      this.businessSummary = {};
      this.businessConsultations = [];
      this.businessDemands = [];
      this.businessOrders = [];
      this.businessVisible = true;
      try {
        const res = await http.get(`/users/${row.id}/business-records`);
        const data = res.data || {};
        this.businessCustomer = data.customer || {};
        this.businessSummary = data.summary || {};
        this.businessConsultations = data.consultations || [];
        this.businessDemands = data.demands || [];
        this.businessOrders = data.orders || [];
        await this.$nextTick();
        this.layoutBusinessTables();
      } finally {
        this.businessLoading = false;
      }
    },
    layoutBusinessTables() {
      this.$nextTick(() => {
        const tableRefs = {
          consultations: "businessConsultationsTable",
          demands: "businessDemandsTable",
          orders: "businessOrdersTable"
        };
        const table = this.$refs[tableRefs[this.businessActiveTab]];
        if (table && typeof table.doLayout === "function") table.doLayout();
      });
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    async settleCommission(row) {
      await this.$confirm(`确认结算佣金 ¥${this.money(row.amount)} 吗？`, "结算佣金", { type: "warning" });
      await http.post(`/referral-commissions/${row.id}/settle`, {});
      this.$message.success("佣金已结算");
      this.openReferralRecords(this.activeUser);
    },
    async cancelCommission(row) {
      const result = await this.$prompt("请输入取消原因", "取消佣金", { inputValidator: value => Boolean(String(value || "").trim()) || "必须填写取消原因" });
      await http.post(`/referral-commissions/${row.id}/cancel`, { reason: result.value });
      this.$message.success("佣金已取消");
      this.openReferralRecords(this.activeUser);
    },
    bindSourceText(value) {
      const map = {
        share: "分享绑定",
        share_login: "分享登录",
        login: "首次登录",
        invite: "邀请绑定",
        manual: "后台绑定",
        qrcode: "二维码",
        link: "链接绑定"
      };
      return map[value] || value || "-";
    }
  }
};
</script>

<style scoped>
.code-text {
  display: block;
  margin-top: 4px;
  color: #606266;
  font-size: 12px;
}

.danger-text {
  color: #f56c6c;
}

.record-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #303133;
  font-size: 14px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.business-summary {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.summary-grid div {
  padding: 12px 8px;
  text-align: center;
  background: #f5f7fa;
  border-radius: 4px;
}

.summary-grid strong,
.summary-grid span {
  display: block;
}

.summary-grid span {
  margin-top: 5px;
  color: #909399;
  font-size: 12px;
}
</style>
